import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Visibility,
  Schedule,
  Add,
  Person,
  Business,
  Email,
  Phone,
  AttachMoney,
  FileDownload,
} from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState } from '../components/ui';
import agent from '../api/axios';
import { useDebounce, useRoleAccess } from '../hooks';
import { PayeeCardSkeleton } from '../components/Skeletons';
import { usePayees, useDeletePayee } from '../hooks/useApi';

const Payee: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { data: payees = [], isLoading } = usePayees();
  const deleteMutation = useDeletePayee();
  const { canEditPayees, canDeletePayees } = useRoleAccess();

  const [open, setOpen] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [payeeToDelete, setPayeeToDelete] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const handleNewPayee = () => setOpen(true);

  const handleChoose = (type: 'business' | 'individual') => {
    setOpen(false);
    navigate('/CreatePayee', { state: { type } });
  };

  const handleDeleteClick = (payee: any) => {
    setPayeeToDelete(payee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (payeeToDelete) {
      deleteMutation.mutate(payeeToDelete.id);
    }
    setDeleteDialogOpen(false);
    setPayeeToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPayeeToDelete(null);
  };

  const handleExportContractors = async () => {
    try {
      const blob = await agent.Export.downloadContractorsCsv();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'contractors.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting contractors:', err);
      enqueueSnackbar('Failed to export contractors. Please try again.', {
        variant: 'error',
      });
    }
  };

  // Filter payees based on debounced search term
  const filteredPayees = useMemo(() => {
    return payees.filter((payee: any) => {
      const searchLower = debouncedSearch.toLowerCase();
      const name = payee.companyName || `${payee.firstName} ${payee.lastName}`;
      return (
        name.toLowerCase().includes(searchLower) ||
        payee.email?.toLowerCase().includes(searchLower)
      );
    });
  }, [payees, debouncedSearch]);

  const getInitials = (payee: any) => {
    if (payee.companyName) {
      return payee.companyName.charAt(0).toUpperCase();
    }
    return `${payee.firstName?.charAt(0) || ''}${
      payee.lastName?.charAt(0) || ''
    }`.toUpperCase();
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Payees"
          subtitle="Manage your 1099 contractors and their information"
          icon={<PeopleIcon />}
          iconColor="info"
        />
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <PayeeCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Payees"
        subtitle="Manage your 1099 contractors and their information"
        icon={<PeopleIcon />}
        iconColor="info"
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportContractors}
              disabled={payees.length === 0}
              sx={{
                px: 3,
                py: 1.25,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Export CSV
            </Button>
            {canEditPayees && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewPayee}
                sx={{
                  px: 3,
                  py: 1.25,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Add Payee
              </Button>
            )}
          </Box>
        }
      />

      {/* Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search payees by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{
              'aria-label': 'Search payees',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.default',
              },
            }}
          />
          {debouncedSearch && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              Showing {filteredPayees.length} of {payees.length} payees
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Empty State */}
      {filteredPayees.length === 0 && !isLoading && (
        <Card>
          <EmptyState
            icon={<PeopleIcon />}
            title={searchTerm ? 'No payees found' : 'No payees yet'}
            description={
              searchTerm
                ? 'Try adjusting your search criteria'
                : 'Add your first 1099 contractor to get started tracking their hours and payments.'
            }
            actionLabel={!searchTerm ? 'Add Your First Payee' : undefined}
            onAction={!searchTerm ? handleNewPayee : undefined}
          />
        </Card>
      )}

      {/* Payee Cards */}
      <Grid container spacing={3}>
        {filteredPayees.map((payee: any) => (
          <Grid item xs={12} sm={6} lg={4} key={payee.id}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header with Avatar and Type */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor:
                          payee.payeeType === 'Business'
                            ? 'info.main'
                            : 'primary.main',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(payee)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ lineHeight: 1.2 }}
                      >
                        {payee.companyName ||
                          `${payee.firstName} ${payee.lastName}`}
                      </Typography>
                      <Chip
                        label={payee.payeeType || 'Individual'}
                        size="small"
                        icon={
                          payee.payeeType === 'Business' ? (
                            <Business sx={{ fontSize: 14 }} />
                          ) : (
                            <Person sx={{ fontSize: 14 }} />
                          )
                        }
                        sx={{
                          mt: 0.5,
                          height: 24,
                          fontSize: '0.75rem',
                          bgcolor: (theme) =>
                            payee.payeeType === 'Business'
                              ? theme.palette.custom.infoTint
                              : theme.palette.custom.primaryTint,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Contact Info */}
                <Box sx={{ mb: 2 }}>
                  {payee.email && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {payee.email}
                      </Typography>
                    </Box>
                  )}
                  {payee.phoneNumber && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {payee.phoneNumber}
                      </Typography>
                    </Box>
                  )}
                  {payee.hourlyRate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoney
                        sx={{ fontSize: 16, color: 'success.main' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        ${payee.hourlyRate}/hour
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Actions */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Add Timesheet">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate('/timesheet', {
                            state: { payeeId: payee.id },
                          })
                        }
                        sx={{
                          bgcolor: (theme) => theme.palette.custom.primaryTint,
                          '&:hover': {
                            bgcolor: (theme) =>
                              theme.palette.custom.primaryTintHover,
                          },
                        }}
                      >
                        <Schedule fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => setSelectedPayee(payee)}
                        sx={{
                          bgcolor: (theme) => theme.palette.custom.infoTint,
                          '&:hover': {
                            bgcolor: (theme) =>
                              theme.palette.custom.infoTintHover,
                          },
                        }}
                      >
                        <Visibility fontSize="small" color="info" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {canEditPayees && (
                      <Tooltip title="Edit Payee">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate('/editpayee', { state: { payee: payee } })
                          }
                          sx={{
                            '&:hover': {
                              bgcolor: (theme) =>
                                theme.palette.custom.warningTintHover,
                            },
                          }}
                        >
                          <Edit fontSize="small" color="warning" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeletePayees && (
                      <Tooltip title="Delete Payee">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(payee)}
                          sx={{
                            '&:hover': {
                              bgcolor: (theme) =>
                                theme.palette.custom.errorTintHover,
                            },
                          }}
                        >
                          <Delete fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Choose Payee Type Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, minWidth: 400 },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Add New Payee
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            What type of payee are you adding?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Card
                onClick={() => handleChoose('individual')}
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  p: 3,
                  transition: 'all 0.2s',
                  border: '2px solid transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => theme.palette.custom.primaryTint,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Person sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  Individual
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A person contractor
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                onClick={() => handleChoose('business')}
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  p: 3,
                  transition: 'all 0.2s',
                  border: '2px solid transparent',
                  '&:hover': {
                    borderColor: 'info.main',
                    bgcolor: (theme) => theme.palette.custom.infoTint,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'info.main',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Business sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  Business
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A company or LLC
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* View Payee Details Dialog */}
      <Dialog
        open={!!selectedPayee}
        onClose={() => setSelectedPayee(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor:
                  selectedPayee?.payeeType === 'Business'
                    ? 'info.main'
                    : 'primary.main',
              }}
            >
              {selectedPayee && getInitials(selectedPayee)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {selectedPayee?.companyName ||
                  `${selectedPayee?.firstName} ${selectedPayee?.lastName}`}
              </Typography>
              <Chip
                label={selectedPayee?.payeeType || 'Individual'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayee && (
            <Grid container spacing={2}>
              {selectedPayee.companyName && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Company Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayee.companyName}
                  </Typography>
                </Grid>
              )}
              {selectedPayee.firstName && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    First Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayee.firstName}
                  </Typography>
                </Grid>
              )}
              {selectedPayee.lastName && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayee.lastName}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  TIN (Tax ID)
                </Typography>
                <Typography variant="body1">
                  {selectedPayee.tin || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Hourly Rate
                </Typography>
                <Typography variant="body1">
                  {selectedPayee.hourlyRate
                    ? `$${selectedPayee.hourlyRate}`
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {selectedPayee.email || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1">
                  {selectedPayee.phoneNumber || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1">
                  {selectedPayee.streetAddress}
                  {selectedPayee.address2 && `, ${selectedPayee.address2}`}
                </Typography>
                <Typography variant="body1">
                  {selectedPayee.city}, {selectedPayee.state}{' '}
                  {selectedPayee.zipCode}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelectedPayee(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              setSelectedPayee(null);
              navigate('/editpayee', { state: { payee: selectedPayee } });
            }}
          >
            Edit Payee
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 3, maxWidth: 450 },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Delete Payee?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>
              {payeeToDelete?.companyName ||
                `${payeeToDelete?.firstName ?? ''} ${
                  payeeToDelete?.lastName ?? ''
                }`}
            </strong>
            ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              All payee information
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Associated timesheet records
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Payment history
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payee;
